import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { DealCategory } from "@/core/entities/deal";
import { PaymentOption, ProductType, type ProductDataInput } from "@/core/entities/product";
import { useEffect } from "react";
import { useFieldArray, UseFormReturn } from "react-hook-form";
import { ImageUpload } from "../ui/image-upload";
import { SpecialAttributePoolEditor } from "@/components/special-attributes";

interface ProductFormProps {
  form: UseFormReturn<ProductDataInput>;
  onSubmit: () => Promise<void>;
  onCancel?: () => void;
  isLoading?: boolean;
  loadingText?: string;
  submitText: string;
  onImageChange: (file: File | null) => void;
  imageUrl?: string;
}

export const ProductForm = ({
  form,
  onSubmit,
  onCancel,
  onImageChange,
  imageUrl,
}: ProductFormProps) => {
  const {
    fields: sellPriceFields,
    append: appendSellPrice,
    remove: removeSellPrice,
  } = useFieldArray({
    control: form.control,
    name: "sellPrices",
  });

  const {
    fields: buyPriceFields,
    append: appendBuyPrice,
    remove: removeBuyPrice,
  } = useFieldArray({
    control: form.control,
    name: "buyPrices",
  });

  const {
    fields: statusTierFields,
    append: appendStatusTier,
    remove: removeStatusTier,
  } = useFieldArray({
    control: form.control,
    name: "statusTiers",
  });

  const { getValues, setValue } = form;
  const watchedBuyOrSell = form.watch("buyOrSell");
  const watchedMinimumValueForSell = form.watch("minimumValueForSell");
  const watchedMaximumValueForSell = form.watch("maximumValueForSell");
  const watchedMinimumValueForBuy = form.watch("minimumValueForBuy");
  const watchedMaximumValueForBuy = form.watch("maximumValueForBuy");

  useEffect(() => {
    const currentSellPrices = getValues("sellPrices");
    if (currentSellPrices && currentSellPrices.length > 0) {
      setValue("sellPrices.0.minRange", watchedMinimumValueForSell);
    }
  }, [watchedMinimumValueForSell, setValue, getValues]);

  useEffect(() => {
    const currentSellPrices = getValues("sellPrices");

    if (currentSellPrices && currentSellPrices.length > 0) {
      const lastIndex = currentSellPrices.length - 1;
      setValue(
        `sellPrices.${lastIndex}.maxRange`,
        watchedMaximumValueForSell,
      );
    }
  }, [watchedMaximumValueForSell, setValue, getValues]);

  useEffect(() => {
    const currentBuyPrices = getValues("buyPrices");
    if (currentBuyPrices && currentBuyPrices.length > 0) {
      setValue("buyPrices.0.minRange", watchedMinimumValueForBuy);
    }
  }, [watchedMinimumValueForBuy, setValue, getValues]);

  useEffect(() => {
    const currentBuyPrices = getValues("buyPrices");
    if (currentBuyPrices && currentBuyPrices.length > 0) {
      const lastIndex = currentBuyPrices.length - 1;
      setValue(
        `buyPrices.${lastIndex}.maxRange`,
        watchedMaximumValueForBuy,
      );
    }
  }, [watchedMaximumValueForBuy, setValue, getValues]);

  const addSellPriceRange = () => {
    const maximumValueForSell = form.getValues("maximumValueForSell");

    appendSellPrice({
      minRange: 0,
      maxRange: maximumValueForSell,
      price: 0,
    });
  };

  const deleteSellPriceRange = (index: number) => {
    if (sellPriceFields.length > 1) {
      removeSellPrice(index);
    }
  };

  const addBuyPriceRange = () => {
    const maximumValueForBuy = form.getValues("maximumValueForBuy");

    appendBuyPrice({ minRange: 0, maxRange: maximumValueForBuy, price: 0 });
  };

  const deleteBuyPriceRange = (index: number) => {
    if (buyPriceFields.length > 1) {
      removeBuyPrice(index);
    }
  };

  const addStatusTier = () => {
    appendStatusTier({ name: "", percentage: 0 });
  };

  const deleteStatusTier = (index: number) => {
    removeStatusTier(index);
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Product Name</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., American Airlines AAdvantage Miles"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="productCode"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Product Code</FormLabel>
                <FormControl>
                  <Input
                    placeholder="e.g., AC, AAdv (defaults to initials)"
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="category"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Category</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {Object.values(DealCategory)
                      .filter((value) => value !== DealCategory.Disqualified)
                      .map((value) => (
                        <SelectItem key={value} value={value}>
                          {value}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FormField
            control={form.control}
            name="buyOrSell"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Operation Type</FormLabel>
                <Select onValueChange={field.onChange} value={field.value}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={ProductType.BUY}>Buy Only</SelectItem>
                    <SelectItem value={ProductType.SELL}>Sell Only</SelectItem>
                    <SelectItem value={ProductType.BOTH}>Buy & Sell</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="inactive"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Status</FormLabel>
                <Select
                  onValueChange={(value) =>
                    field.onChange(value === "inactive")
                  }
                  value={field.value ? "inactive" : "active"}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="paymentOption"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Payment Option</FormLabel>
              <Select
                onValueChange={field.onChange}
                value={field.value as PaymentOption | undefined ?? ""}
              >
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment option" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value={PaymentOption.PAY_IN_ADVANCE}>
                    Pay in Advance
                  </SelectItem>
                  <SelectItem value={PaymentOption.PAY_ONCE_BOOKED}>
                    Pay Once Booked
                  </SelectItem>
                  <SelectItem value={PaymentOption.PAY_AFTER_BOOKING}>
                    Pay After Booking
                  </SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="space-y-2">
          <Label>Special Attributes</Label>
          <SpecialAttributePoolEditor value={[]} onChange={() => {}} />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <FormField
            control={form.control}
            name="usernameLabelOverride"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Username Label Override</FormLabel>
                <FormControl>
                  <Input
                    placeholder='Default: "Username"'
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="emailLabelOverride"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email Label Override</FormLabel>
                <FormControl>
                  <Input
                    placeholder='Default: "Email"'
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="passwordLabelOverride"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Password Label Override</FormLabel>
                <FormControl>
                  <Input
                    placeholder='Default: "Password"'
                    {...field}
                    value={field.value ?? ""}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Separator />

        <FormField
          control={form.control}
          name="logo"
          render={({ field }) => (
            <FormItem className="py-2">
              <FormLabel>Product Image</FormLabel>
              <FormControl>
                <ImageUpload
                  onImageChange={onImageChange}
                  imageUrl={imageUrl || field.value}
                  onLogoUrlRemove={() => {
                    field.onChange("");
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {(watchedBuyOrSell === ProductType.SELL ||
          watchedBuyOrSell === ProductType.BOTH) && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 align-top">
              <FormField
                control={form.control}
                name="minimumValueForSell"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Minimum Value for Sell</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter minimum value"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value) || 0)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maximumValueForSell"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximum Value for Sell</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter maximum value"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value) || 0)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-3">
              <Label>Product Prices For Sell</Label>
              <div className="overflow-x-auto">
                <Table className="min-w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Min Range</TableHead>
                      <TableHead>Max Range</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sellPriceFields.map((field, index) => (
                      <TableRow key={field.id}>
                        <TableCell>
                          <FormField
                            control={form.control}
                            name={`sellPrices.${index}.minRange`}
                            render={({ field }) => (
                              <FormControl>
                                <Input
                                  type="number"
                                  placeholder="Min"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                />
                              </FormControl>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <FormField
                            control={form.control}
                            name={`sellPrices.${index}.maxRange`}
                            render={({ field }) => (
                              <FormControl>
                                <Input
                                  type="number"
                                  placeholder="Max"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                />
                              </FormControl>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <FormField
                            control={form.control}
                            name={`sellPrices.${index}.price`}
                            render={({ field }) => (
                              <FormControl>
                                <Input
                                  type="number"
                                  placeholder="Price"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                />
                              </FormControl>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteSellPriceRange(index)}
                            disabled={sellPriceFields.length === 1}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addSellPriceRange}
                className="w-full sm:w-auto"
              >
                Add New Row
              </Button>
            </div>
          </>
        )}

        {(watchedBuyOrSell === ProductType.BUY ||
          watchedBuyOrSell === ProductType.BOTH) && (
          <>
            {watchedBuyOrSell === ProductType.BOTH && <Separator />}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="minimumValueForBuy"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Minimum Value for Buy</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter minimum value"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value) || 0)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="maximumValueForBuy"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Maximum Value for Buy</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="Enter maximum value"
                        {...field}
                        onChange={(e) =>
                          field.onChange(parseFloat(e.target.value) || 0)
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="space-y-3">
              <Label>Product Prices For Buy</Label>
              <div className="overflow-x-auto">
                <Table className="min-w-full">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Min Range</TableHead>
                      <TableHead>Max Range</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {buyPriceFields.map((field, index) => (
                      <TableRow key={field.id}>
                        <TableCell>
                          <FormField
                            control={form.control}
                            name={`buyPrices.${index}.minRange`}
                            render={({ field }) => (
                              <FormControl>
                                <Input
                                  type="number"
                                  placeholder="Min"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                />
                              </FormControl>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <FormField
                            control={form.control}
                            name={`buyPrices.${index}.maxRange`}
                            render={({ field }) => (
                              <FormControl>
                                <Input
                                  type="number"
                                  placeholder="Max"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                />
                              </FormControl>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <FormField
                            control={form.control}
                            name={`buyPrices.${index}.price`}
                            render={({ field }) => (
                              <FormControl>
                                <Input
                                  type="number"
                                  step="0.0001"
                                  placeholder="Price"
                                  {...field}
                                  onChange={(e) =>
                                    field.onChange(
                                      parseFloat(e.target.value) || 0,
                                    )
                                  }
                                />
                              </FormControl>
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => deleteBuyPriceRange(index)}
                            disabled={buyPriceFields.length === 1}
                          >
                            Delete
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={addBuyPriceRange}
                className="w-full sm:w-auto"
              >
                Add New Row
              </Button>
            </div>
          </>
        )}

        <Separator />

        <div className="space-y-3">
          <Label>Status Tiers</Label>
          {statusTierFields.length > 0 && (
            <div className="overflow-x-auto">
              <Table className="min-w-full">
                <TableHeader>
                  <TableRow>
                    <TableHead>Tier Name</TableHead>
                    <TableHead>Percentage</TableHead>
                    <TableHead>Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {statusTierFields.map((field, index) => (
                    <TableRow key={field.id}>
                      <TableCell>
                        <FormField
                          control={form.control}
                          name={`statusTiers.${index}.name`}
                          render={({ field }) => (
                            <FormControl>
                              <Input
                                placeholder="e.g., Bronze, Silver, Gold"
                                {...field}
                              />
                            </FormControl>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <FormField
                          control={form.control}
                          name={`statusTiers.${index}.percentage`}
                          render={({ field }) => (
                            <FormControl>
                              <Input
                                placeholder="e.g., 5%, 10%, 15%"
                                type="number"
                                min={0}
                                {...field}
                                onChange={(e) =>
                                  field.onChange(
                                    parseFloat(e.target.value) || 0,
                                  )
                                }
                              />
                            </FormControl>
                          )}
                        />
                      </TableCell>
                      <TableCell>
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => deleteStatusTier(index)}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <Button
            type="button"
            variant="outline"
            onClick={addStatusTier}
            className="w-full sm:w-auto"
          >
            Add Status Tier
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
          {onCancel && (
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
          )}
          <Button type="submit" className="w-full sm:w-auto">
            Submit
          </Button>
        </div>
      </form>
    </Form>
  );
};
